import {
    getArrayOfSegments,
    getArrayOfSegmentsId,
    getFirstSevenSegments,
    getSegmentsAndWebsitesByGroup,
    getSegmentsIdsFromGroup,
} from "app/components/dashboard/widget-wizard/components/segments/helpers";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    selectCustomSegmentsGroups,
    selectAccountSegments,
} from "app/components/dashboard/widget-wizard/components/segments/selectors";
import type { SelectedSegmentsId } from "app/components/dashboard/widget-wizard/components/segments/types";
import type { ICustomSegmentAccount } from "app/services/segments/segmentsApiService";
import { templateSetKeys, templateSetSegmentIds } from "../actions/dashboardTemplateActions";
import debounce from "lodash/debounce";
import { WEBSITE_ID_KEY } from "app/components/dashboard/widget-wizard/components/segments/constants";

const useSegmentsTemplates = () => {
    const dispatch = useDispatch();

    const accountSegments = useSelector(selectAccountSegments);
    const segmentsGroups = useSelector(selectCustomSegmentsGroups);

    const [segments, setSegments] = useState<ICustomSegmentAccount[] | []>([]);
    const [selectedSegments, setSelectedSegments] = useState<ICustomSegmentAccount[]>([]);

    const [selectedSegmentsIds, setSelectedSegmentsIds] = useState<SelectedSegmentsId | Record<string, never>>({});

    const [selectedGroupSegments, setSelectedGroupSegments] = useState<ICustomSegmentAccount[]>([]);

    const handleSegmentsGroupSelect = async (groupId: string) => {
        if (segmentsGroups) {
            const segmentsIdsFromGroup = getSegmentsIdsFromGroup(groupId, segmentsGroups);
            const segmentsIds = getFirstSevenSegments(segmentsIdsFromGroup.ids);
            const arrayOfSelectedSegments = getArrayOfSegmentsId(segmentsIds);

            const hasWebsites =
                arrayOfSelectedSegments && !!arrayOfSelectedSegments?.find((id: string) => id.includes(WEBSITE_ID_KEY));

            const segmentsAndWebsitesByGroup = getSegmentsAndWebsitesByGroup(
                accountSegments,
                segmentsGroups,
                groupId,
                segmentsIdsFromGroup,
                hasWebsites,
            );

            const preselectedFirstSevenSegments = getFirstSevenSegments(segmentsIdsFromGroup.ids);
            const preselectedFirstSevenSegmentsArray = getArrayOfSegments(
                preselectedFirstSevenSegments,
                segmentsAndWebsitesByGroup,
            );

            setSelectedSegmentsIds(preselectedFirstSevenSegments);
            setSelectedSegments(preselectedFirstSevenSegmentsArray);

            setSegments(segmentsAndWebsitesByGroup);
            setSelectedGroupSegments(segmentsAndWebsitesByGroup);

            dispatch(templateSetKeys(arrayOfSelectedSegments));
            dispatch(templateSetSegmentIds(arrayOfSelectedSegments));
        }
    };

    const setSelectedSegmentsId = debounce((segment: ICustomSegmentAccount) => {
        if (selectedSegmentsIds[segment.id]) {
            setSelectedSegmentsIds((state) => {
                const newState = { ...state };

                delete newState[segment.id];

                return newState;
            });

            const newSegentsIds = [...Object.keys(selectedSegmentsIds)].filter((id: string) => id !== segment.id);

            const filteredSegements = getArrayOfSegments(newSegentsIds, selectedGroupSegments);

            setSelectedSegments(filteredSegements);

            dispatch(templateSetKeys(newSegentsIds));
            dispatch(templateSetSegmentIds(newSegentsIds));
        } else {
            const newSegentsIds = [...Object.keys(selectedSegmentsIds), segment.id];

            setSelectedSegmentsIds((state) => ({
                ...state,
                [segment.id]: true,
            }));
            setSelectedSegments((state) => [...state, segment]);

            dispatch(templateSetKeys(newSegentsIds));
            dispatch(templateSetSegmentIds(newSegentsIds));
        }
    }, 100);

    return {
        segments,
        selectedSegments,
        selectedSegmentsIds,
        setSelectedSegmentsId,
        handleSegmentsGroupSelect,
    };
};

export default useSegmentsTemplates;
